import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { 
  ArrowLeft, Download, Play, ShoppingCart, Check, Loader2, 
  Package2, Sparkles, FileText, Clock, Ruler, Scissors
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import PublicNavbar from '@/components/PublicNavbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DesignDetail() {
  const { designId } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [email, setEmail] = useState('');
  const [hasPurchased, setHasPurchased] = useState(false);
  const [downloadToken, setDownloadToken] = useState('');  // SEC-001 FIX: Store download token
  const [checkingPurchase, setCheckingPurchase] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [paidContent, setPaidContent] = useState(null);  // SEC-001 FIX: Store fetched paid content

  const loadDesign = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/designs/${designId}`);
      setDesign(response.data);
      
      // Check if it's free - show instructions immediately
      if (response.data.is_free) {
        setShowInstructions(true);
      }
    } catch (error) {
      logger.error('Failed to load design:', error);
      toast.error('Design not found');
      navigate('/designs');
    } finally {
      setLoading(false);
    }
  }, [designId, navigate]);

  useEffect(() => {
    loadDesign();
  }, [loadDesign]);

  const checkPurchaseStatus = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setCheckingPurchase(true);
    try {
      const response = await axios.get(
        `${API}/designs/${design.design_id}/verify-purchase?email=${encodeURIComponent(email)}`
      );
      
      if (response.data.has_purchased) {
        setHasPurchased(true);
        setDownloadToken(response.data.download_token || '');  // SEC-001 FIX: Store token
        
        // SEC-001 FIX: Fetch the paid content using the download token
        try {
          const contentResponse = await axios.get(
            `${API}/designs/${design.design_id}/download?email=${encodeURIComponent(email)}&token=${encodeURIComponent(response.data.download_token)}`
          );
          if (contentResponse.data.allowed) {
            setPaidContent({
              instructions: contentResponse.data.instructions,
              pdf_url: contentResponse.data.pdf_url
            });
          }
        } catch (contentError) {
          logger.error('Failed to fetch paid content:', contentError);
        }
        
        setShowInstructions(true);
        toast.success('Access granted! You can now view the full instructions.');
      } else {
        toast.error('No purchase found for this email. Please purchase to access.');
      }
    } catch (error) {
      logger.error('Failed to verify purchase:', error);
      toast.error('Failed to verify purchase');
    } finally {
      setCheckingPurchase(false);
    }
  };

  const handlePurchase = async () => {
    if (!email) {
      toast.error('Please enter your email to purchase');
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/designs/${design.design_id}/purchase`, {
        email: email,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      logger.error('Failed to start purchase:', error);
      toast.error(error.response?.data?.detail || 'Failed to process purchase');
      setPurchasing(false);
    }
  };

  const handleDownloadPdf = async () => {
    // SEC-001 FIX: Use paidContent for paid designs, design for free designs
    const pdfUrl = design.is_free ? design.pdf_url : paidContent?.pdf_url;
    if (!pdfUrl) {
      toast.error('PDF not available');
      return;
    }

    try {
      if (design.is_free) {
        window.open(pdfUrl, '_blank');
      } else {
        // For paid designs, we already have the URL from paidContent
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!design) return null;

  const canAccessContent = design.is_free || hasPurchased;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNavbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images Section */}
          <div>
            <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-rose-100 to-orange-100 aspect-square mb-4">
              {design.images && design.images.length > 0 ? (
                <img
                  src={design.images[selectedImage]?.url}
                  alt={design.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Sparkles className="h-24 w-24 text-primary/50" />
                </div>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {design.images && design.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {design.images.map((img, idx) => (
                  <button
                    key={img.url}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === idx ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* YouTube Video */}
            {design.youtube_video_id && (
              <div className="mt-6">
                <h3 className="text-lg font-heading font-semibold mb-3 flex items-center">
                  <Play className="h-5 w-5 mr-2 text-primary" />
                  Video Tutorial
                </h3>
                <div className="rounded-2xl overflow-hidden aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${design.youtube_video_id}`}
                    title="Video Tutorial"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>

          {/* Details Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(design.difficulty)}`}>
                {design.difficulty}
              </span>
              <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-700 text-sm capitalize">
                {design.category}
              </span>
              {design.is_free && (
                <span className="px-3 py-1 rounded-full bg-green-500 text-white text-sm font-bold">
                  FREE
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight mb-4">
              {design.title}
            </h1>

            <p className="text-lg text-muted-foreground mb-6">
              {design.description}
            </p>

            {/* Pattern Details */}
            <div className="rounded-2xl bg-stone-50 p-6 mb-6">
              <h3 className="font-heading font-semibold mb-4">Pattern Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {design.hook_size && (
                  <div className="flex items-center">
                    <Scissors className="h-4 w-4 mr-2 text-primary" />
                    <span>Hook: {design.hook_size}</span>
                  </div>
                )}
                {design.yarn_weight && (
                  <div className="flex items-center">
                    <Package2 className="h-4 w-4 mr-2 text-primary" />
                    <span>Yarn: {design.yarn_weight}</span>
                  </div>
                )}
                {design.finished_size && (
                  <div className="flex items-center">
                    <Ruler className="h-4 w-4 mr-2 text-primary" />
                    <span>Size: {design.finished_size}</span>
                  </div>
                )}
                {design.pdf_url && (
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-primary" />
                    <span>PDF Included</span>
                  </div>
                )}
              </div>
              {design.materials_needed && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <h4 className="font-medium mb-2">Materials Needed:</h4>
                  <p className="text-muted-foreground text-sm whitespace-pre-line">
                    {design.materials_needed}
                  </p>
                </div>
              )}
            </div>

            {/* Purchase/Access Section */}
            {!design.is_free && !hasPurchased ? (
              <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-heading font-bold text-primary">
                    €{design.price}
                  </span>
                  <span className="text-sm text-muted-foreground">One-time purchase</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Your purchase will be linked to this email
                    </p>
                  </div>

                  <Button
                    onClick={handlePurchase}
                    disabled={purchasing || !email}
                    className="w-full rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all font-bold py-6"
                  >
                    {purchasing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        Buy Now - €{design.price}
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Already purchased?</p>
                    <Button
                      onClick={checkPurchaseStatus}
                      disabled={checkingPurchase || !email}
                      variant="outline"
                      className="rounded-full"
                    >
                      {checkingPurchase ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Verify Purchase
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6">
                <div className="flex items-center text-green-700 mb-4">
                  <Check className="h-6 w-6 mr-2" />
                  <span className="font-semibold">
                    {design.is_free ? 'Free Pattern!' : 'You have access to this pattern'}
                  </span>
                </div>
                
                {(design.is_free ? design.pdf_url : paidContent?.pdf_url) && (
                  <Button
                    onClick={handleDownloadPdf}
                    className="w-full rounded-full bg-green-600 hover:bg-green-700 text-white font-bold"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            )}

            {/* Tags */}
            {design.tags && design.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {design.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm cursor-pointer hover:bg-stone-200 transition-colors"
                    onClick={() => navigate(`/designs?search=${encodeURIComponent(tag)}`)}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Instructions Section */}
        {canAccessContent && (design.is_free ? design.instructions : paidContent?.instructions) && (
          <div className="mt-12 rounded-3xl bg-white border border-stone-100 p-8 shadow-sm">
            <h2 className="text-2xl font-heading font-bold mb-6 flex items-center">
              <FileText className="h-6 w-6 mr-3 text-primary" />
              Pattern Instructions
            </h2>
            <div 
              className="prose prose-stone max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(
                  (design.is_free ? design.instructions : paidContent?.instructions || '').replace(/\n/g, '<br/>')
                ) 
              }}
            />
          </div>
        )}

        {!canAccessContent && (
          <div className="mt-12 rounded-3xl bg-stone-100 border border-stone-200 p-12 text-center">
            <Sparkles className="h-16 w-16 text-stone-400 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-2">Full Instructions</h3>
            <p className="text-muted-foreground">
              Purchase this pattern to view the complete instructions, download the PDF, and access all materials.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. Made with love for yarn crafters.
          </p>
        </div>
      </footer>
    </div>
  );
}
